import { generateEmailHead } from './generateEmailHead';
import { generateEmailBody } from './generateEmailBody';
import { generateLogo } from './generateLogo';
// The 600px email column less its 20px padding each side
const CONTENT_WIDTH = 560;
// Shrinks a pixel width over the column to fit it, scaling its height to match
function shrinkWidth(html, width, height) {
    var _a;
    const value = Number((_a = html.match(width)) === null || _a === void 0 ? void 0 : _a[2]);
    if (!(value > CONTENT_WIDTH))
        return html;
    return html
        .replace(width, (_, before) => `${before}${CONTENT_WIDTH}`)
        .replace(height, (_, before, h) => `${before}${Math.round(Number(h) * CONTENT_WIDTH / value)}`);
}
/**
 * Pasted signatures often carry images at their full pixel size (width="2000"), which push the email
 * wider than the reader's screen. Caps every image at the email's width, keeping its aspect ratio.
 * Outlook desktop ignores max-width, so pixel widths over the column are shrunk too.
 */
export function fitSignatureImages(signatureHtml) {
    const fit = 'max-width:100%;height:auto;';
    return signatureHtml.replace(/<img\b[^>]*>/gi, tag => {
        const img = shrinkWidth(tag, /(\swidth\s*=\s*["']?)(\d+(?:\.\d+)?)/i, /(\sheight\s*=\s*["']?)(\d+(?:\.\d+)?)/i);
        const style = img.match(/\sstyle\s*=\s*(["'])(.*?)\1/i);
        if (!style)
            return img.replace(/^<img\b/i, `<img style="${fit}"`);
        const css = shrinkWidth(style[2], /((?:^|;)\s*width\s*:\s*)(\d+(?:\.\d+)?)(?=px)/i, /((?:^|;)\s*height\s*:\s*)(\d+(?:\.\d+)?)(?=px)/i);
        // A cap of 100% or less already fits, anything else (none, 2000px) is overridden by appending ours
        const maxWidth = css.match(/(?:^|;)\s*max-width\s*:\s*(\d+(?:\.\d+)?)%\s*(?:;|$)/i);
        return img.replace(style[0], ` style=${style[1]}${maxWidth && Number(maxWidth[1]) <= 100 ? css : `${css.replace(/;?\s*$/, ';')}${fit}`}${style[1]}`);
    });
}
// Moves the whole signature like the old signature_position did, its own lines stay left aligned
function generateSignatureBlock(signatureHtml, align) {
    if (!signatureHtml)
        return '';
    return /* html */ `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
      <tr>
        <td align="${align}" style="line-height:normal;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td style="text-align:left;">${signatureHtml}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;
}
/**
 * Swaps {{{Logo}}} and {{{Signature}}} for the sender's logo and signature. A tag alone in a paragraph
 * (how the editor saves it) takes the paragraph's alignment, and the whole paragraph is replaced so the
 * signature's own paragraphs aren't nested in it. A tag inside other text is left aligned.
 */
export function renderSenderBlocks(html, logoUrl, signatureHtml) {
    // Unaligned paragraphs show as left in the editor, so they send as left too
    const render = (tag, align = 'left', width) => (tag === 'Logo' ? generateLogo(logoUrl, align, width) : generateSignatureBlock(signatureHtml, align));
    return html
        .replace(/<p\b([^>]*)>\s*\{\{\{(Logo|Signature)\}\}\}\s*<\/p>/g, (_, attrs, tag) => {
        var _a, _b;
        return render(tag, (_a = attrs.match(/text-align:\s*(left|center|right)/)) === null || _a === void 0 ? void 0 : _a[1], Number((_b = attrs.match(/data-width="(\d+)"/)) === null || _b === void 0 ? void 0 : _b[1]) || undefined);
    })
        .replace(/\{\{\{(Logo|Signature)\}\}\}/g, (_, tag) => render(tag));
}
export function generateHtml(formData, logoUrl, removeBranding, signatureHtml) {
    const data = Object.assign({}, formData);
    if (!data.main_html_content) {
        throw new Error(`No main_html_content found`);
    }
    if (formData.include_upcoming_latefee && formData.secondary_html_content && formData.main_html_content) {
        // We need to preprocess the latefee section,
        data.main_html_content = data.main_html_content.replace(/{{{PenaltiesSection}}}/g, data.secondary_html_content);
        if (!data.main_html_content) {
            throw new Error(`No main_html_content found after preprocessing`);
        }
    }
    if (formData.include_payment_plan_section && formData.payment_plan_html_content && formData.main_html_content) {
        // We need to preprocess the payment plan section,
        data.main_html_content = data.main_html_content.replace(/{{{PaymentPlanSection}}}/g, data.payment_plan_html_content);
        if (!data.main_html_content) {
            throw new Error(`No main_html_content found after preprocessing`);
        }
    }
    signatureHtml = signatureHtml && fitSignatureImages(signatureHtml);
    data.main_html_content = renderSenderBlocks(data.main_html_content, logoUrl, signatureHtml);
    // Moved templates place the logo and signature themselves with {{{Logo}}} and {{{Signature}}}
    const legacyLogoUrl = formData.migrated_to_new_logo_sig ? null : logoUrl;
    const legacySignatureHtml = formData.migrated_to_new_logo_sig ? null : signatureHtml;
    return `
      <!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">
      <html data-editor-version="2" class="sg-campaigns" xmlns="http://www.w3.org/1999/xhtml">
    ${generateEmailHead()}
    ${generateEmailBody(data, legacyLogoUrl, removeBranding, legacySignatureHtml)}
    </html>
  `;
}
//# sourceMappingURL=generateEmailTemplate.js.map